import { useEffect, useState, useMemo } from "react";
import { FaUsers, FaUserPlus, FaCalendar, FaSearch, FaSort, FaTimes } from "react-icons/fa";
import { useReferrals } from "../contexts/ReferralsContext";

const Referrals = () => {
    const { setPendingCount } = useReferrals();
    const [referrals, setReferrals] = useState([]);
    const [servicemen, setServicemen] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [sortBy, setSortBy] = useState("createdAt");
    const [sortOrder, setSortOrder] = useState("desc");
    const referralsPerPage = 10;
    const [selectedServiceman, setSelectedServiceman] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);
    const DEFAULT_IMAGE = "https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png";

    const fetchReferrals = async () => {
        try {

            const [referralsRes, servicemenRes] = await Promise.all([
                fetch("https://us-central1-workexpress-1ffff.cloudfunctions.net/app/referrals"),
                fetch("https://us-central1-workexpress-1ffff.cloudfunctions.net/app/servicemen")
            ]);


            if (!referralsRes.ok || !servicemenRes.ok) {
                throw new Error(`Failed to fetch data - Referrals: ${referralsRes.status}, Servicemen: ${servicemenRes.status}`);
            }

            const [referralsData, servicemenData] = await Promise.all([
                referralsRes.json(),
                servicemenRes.json()
            ]);

            const referralsArray = Array.isArray(referralsData) ? referralsData : [referralsData];
            setReferrals(referralsArray);
            setServicemen(Array.isArray(servicemenData) ? servicemenData : [servicemenData]);
            
            // Calculate and update pending referrals count
            const pendingCount = referralsArray.filter(r => 
                !r.status || r.status.toLowerCase() === 'pending'
            ).length;
            setPendingCount(pendingCount);
            
            setError(null);
            setLoading(false);
        } catch (error) {
            console.error("Error fetching referrals:", error);
            setError(error.message);
            setReferrals([]);
            setServicemen([]);
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReferrals();
        const intervalId = setInterval(fetchReferrals, 10000); // Refresh every 10 seconds
        return () => clearInterval(intervalId);
    }, []);

    // Helper function to get serviceman details by auth ID
    const getServicemanByAuthId = (authId) => {
        return servicemen.find(serviceman => serviceman.id === authId) || null;
    };

    const filteredReferrals = useMemo(() => {
        return referrals
            .filter(referral => {
                if (!searchTerm) return true;

                const search = searchTerm.toLowerCase();
                const phoneNumber = (referral.newUserPhone || "").toLowerCase();
                const formattedPhone = phoneNumber ? `+91-${phoneNumber}`.toLowerCase() : "";

                // Search in both the raw phone number and the formatted version
                return phoneNumber.includes(search) || formattedPhone.includes(search);
            })
            .sort((a, b) => {
                let aValue, bValue;

                if (sortBy === "createdAt") {
                    aValue = a.createdAt?._seconds || a.createdAt || 0;
                    bValue = b.createdAt?._seconds || b.createdAt || 0;
                } else if (sortBy === "referrerName") {
                    const aReferrer = getServicemanByAuthId(a.referrerAuthId);
                    const bReferrer = getServicemanByAuthId(b.referrerAuthId);
                    aValue = (aReferrer?.name || "").toLowerCase();
                    bValue = (bReferrer?.name || "").toLowerCase();
                } else if (sortBy === "referredName") {
                    const aReferred = getServicemanByAuthId(a.newAuthId);
                    const bReferred = getServicemanByAuthId(b.newAuthId);
                    aValue = (aReferred?.name || "").toLowerCase();
                    bValue = (bReferred?.name || "").toLowerCase();
                } else {
                    aValue = a[sortBy] || "";
                    bValue = b[sortBy] || "";
                }

                if (sortOrder === "asc") {
                    return aValue > bValue ? 1 : -1;
                } else {
                    return aValue < bValue ? 1 : -1;
                }
            });
    }, [referrals, servicemen, searchTerm, sortBy, sortOrder]);

    const paginatedReferrals = useMemo(() => {
        const indexOfLastReferral = currentPage * referralsPerPage;
        const indexOfFirstReferral = indexOfLastReferral - referralsPerPage;
        return {
            indexOfFirstReferral,
            currentReferrals: filteredReferrals.slice(indexOfFirstReferral, indexOfLastReferral),
            totalPages: Math.ceil(filteredReferrals.length / referralsPerPage),
        };
    }, [filteredReferrals, currentPage]);

    const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

    const handleSortChange = (e) => {
        const newSortBy = e.target.value;
        if (sortBy === newSortBy) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortBy(newSortBy);
            setSortOrder("desc");
        }
        setCurrentPage(1);
    };

    const handleApproveReferral = async (referralId) => {
        if (!window.confirm("Are you sure you want to approve this referral?")) return;

        try {
            const response = await fetch(`https://us-central1-workexpress-1ffff.cloudfunctions.net/app/referrals/${referralId}/approve`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ status: 'approved' })
            });

            if (!response.ok) {
                throw new Error('Failed to approve referral');
            }

            // Refresh the data
            fetchReferrals();
            alert('Referral approved successfully!');
        } catch (error) {
            console.error('Error approving referral:', error);
            alert('Failed to approve referral');
        }
    };

    const handleDeclineReferral = async (referralId) => {
        if (!window.confirm("Are you sure you want to decline this referral?")) return;

        try {
            const response = await fetch(`https://us-central1-workexpress-1ffff.cloudfunctions.net/app/referrals/${referralId}/decline`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ status: 'declined' })
            });

            if (!response.ok) {
                throw new Error('Failed to decline referral');
            }

            // Refresh the data
            fetchReferrals();
            alert('Referral declined successfully!');
        } catch (error) {
            console.error('Error declining referral:', error);
            alert('Failed to decline referral');
        }
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return "N/A";

        let date;
        if (timestamp._seconds) {
            date = new Date(timestamp._seconds * 1000);
        } else if (timestamp instanceof Date) {
            date = timestamp;
        } else {
            date = new Date(timestamp);
        }

        return date.toLocaleDateString() + " " + date.toLocaleTimeString();
    };

    const handleServicemanClick = async (authId) => {
        if (!authId) return;

        setModalLoading(true);
        setSelectedServiceman(null);

        try {
            const response = await fetch(`https://us-central1-workexpress-1ffff.cloudfunctions.net/app/servicemen/${authId}`);
            if (!response.ok) {
                throw new Error("Failed to fetch serviceman details");
            }
            const servicemanData = await response.json();
            setSelectedServiceman(servicemanData);
        } catch (error) {
            console.error("Error fetching serviceman details:", error);
            alert("Failed to load serviceman details");
        } finally {
            setModalLoading(false);
        }
    };

    const closeModal = () => {
        setSelectedServiceman(null);
    };

    // Helper function to check if serviceman is approved (handles different data types and field names)
    const isServicemanApproved = (serviceman) => {
        if (!serviceman) return false;
        const approved = serviceman.approved || serviceman.Approved || serviceman.APPROVED;
        if (approved === true || approved === 'true' || approved === 'True' || approved === 'TRUE') {
            return true;
        }
        return false;
    };

    if (loading) {
        return (
            <div className="loading">
                <span className="loader"></span>
                <style>{`
          .loading {
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
            color: var(--text-secondary, #666);
            flex-direction: column;
          }
          .loader {
            width: 48px;
            height: 48px;
            border-radius: 50%;
            display: inline-block;
            position: relative;
            border: 2px solid #FF3D00;
            box-sizing: border-box;
            animation: rotation 1s linear infinite;
          }
          .loader::after {
            content: '';  
            box-sizing: border-box;
            position: absolute;
            left: 4px;
            top: 4px;
            border: 2px solid #000;
            width: 12px;
            height: 12px;
            border-radius: 50%;
          }
          [data-theme="dark"] .loader::after {
            border: 2px solid #FFF;
          }
          @keyframes rotation {
            0% {
              transform: rotate(0deg);
            }
            100% {
              transform: rotate(360deg);
            }
          }
        `}</style>
            </div>
        );
    }

    // Debug logging

    if (error) {
        return (
            <div className="error-container">
                <h2>Error Loading Referrals</h2>
                <p>{error}</p>
                <button onClick={fetchReferrals} style={{
                    padding: '10px 20px',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    marginTop: '10px'
                }}>
                    Retry
                </button>
                <style>{`
                    .error-container {
                        padding: 40px;
                        text-align: center;
                        color: #dc2626;
                        background: #fef2f2;
                        border-radius: 8px;
                        margin: 20px;
                    }
                `}</style>
            </div>
        );
    }

    return (
        <div className="referrals-panel">
            <style>{`
        .referrals-panel {
          padding: 30px;
          margin: 0 auto;
          background: var(--bg-primary, #f9fafb);
          border-radius: 8px;
          box-shadow: 0 2px 4px var(--shadow, rgba(0,0,0,0.05));
        }
        .header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding-bottom: 15px;
          border-bottom: 1px solid var(--border-color, #e5e7eb);
        }
        h2 {
          color: var(--text-primary, #1f2937);
          font-size: 24px;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .search-sort {
          display: flex;
          gap: 10px;
          align-items: center;
        }
        .search-input {
          padding: 8px 12px;
          border: 1px solid var(--border-color, #d1d5db);
          border-radius: 4px;
          font-size: 14px;
          width: 250px;
          transition: all 0.2s;
          background: var(--bg-input, #ffffff);
          color: var(--text-primary, #111827);
        }
        .search-input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 4px rgba(59,130,246,0.3);
        }
        .sort-btn {
          padding: 8px 12px;
          border: 1px solid var(--border-color, #d1d5db);
          border-radius: 4px;
          background: var(--bg-input, #fff);
          font-size: 14px;
          cursor: pointer;
          color: var(--text-primary, #374151);
          display: flex;
          align-items: center;
          gap: 5px;
        }
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 20px;
          margin-bottom: 30px;
        }
        .stat-card {
          background: var(--bg-card, #ffffff);
          padding: 20px;
          border-radius: 8px;
          box-shadow: 0 2px 4px var(--shadow, rgba(0,0,0,0.1));
          text-align: center;
        }
        .stat-icon {
          font-size: 2rem;
          margin-bottom: 10px;
          color: #3b82f6;
        }
        .stat-value {
          font-size: 2rem;
          font-weight: bold;
          color: var(--text-primary, #1f2937);
          margin: 0;
        }
        .stat-label {
          color: var(--text-muted, #6b7280);
          margin: 5px 0 0 0;
          font-size: 14px;
        }
        .table-container {
          overflow-x: auto;
          min-height: 300px;
          background: var(--bg-card, #ffffff);
          border-radius: 8px;
          box-shadow: 0 2px 4px var(--shadow, rgba(0,0,0,0.1));
        }
        table {
          width: 100%;
          border-collapse: collapse;
          background: var(--bg-card, #ffffff);
        }
        th {
          background: #374151;
          color: #ffffff;
          padding: 15px 12px;
          text-align: left;
          font-weight: 600;
          font-size: 14px;
          border-bottom: 2px solid var(--border-color, #4b5563);
          position: sticky;
          top: 0;
          z-index: 10;
        }
        [data-theme="dark"] th {
          background: #1f2937;
          color: #ffffff;
          border-bottom-color: var(--border-color, #404040);
        }
        td {
          padding: 15px 12px;
          border-bottom: 1px solid var(--border-color, #e5e7eb);
          font-size: 14px;
          color: var(--text-primary, #4a5568);
          max-width: 200px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          background: var(--bg-card, #ffffff);
        }
        .referral-row {
          transition: background 0.2s;
          background: var(--bg-card, #ffffff);
        }
        [data-theme="dark"] .referral-row {
          background: var(--bg-secondary, #2d2d2d);
        }
        .referral-row:hover {
          background: var(--bg-hover, #f9fafb);
        }
        [data-theme="dark"] .referral-row:hover {
          background: var(--bg-hover, #353535);
        }
        .user-info {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .user-details {
          display: flex;
          flex-direction: column;
        }
        .user-name {
          font-weight: 600;
          color: var(--text-primary, #1f2937);
        }
        .clickable-name {
          cursor: pointer;
          color: #3b82f6;
          text-decoration: underline;
          transition: all 0.2s;
        }
        .clickable-name:hover {
          color: #2563eb;
          text-decoration: none;
        }
        .user-email {
          font-size: 12px;
          color: var(--text-muted, #6b7280);
        }
        .status-badge {
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
          text-transform: uppercase;
        }
        .status-badge.active {
          background: #dcfce7;
          color: #166534;
        }
        .status-badge.pending {
          background: #fef3c7;
          color: #92400e;
        }
        .status-badge.completed {
          background: #dbeafe;
          color: #1e40af;
        }
        .action-buttons {
          display: flex;
          gap: 8px;
          justify-content: center;
        }
        .approved-status {
          color: #166534;
          background: #dcfce7;
          padding: 4px 12px;
          border-radius: 12px;
          font-weight: 500;
          display: inline-block;
        }
        .not-approved-status {
          color: #991b1b;
          background: #fee2e2;
          padding: 4px 12px;
          border-radius: 12px;
          font-weight: 500;
          display: inline-block;
        }
        .approve-btn, .decline-btn {
          width: 32px;
          height: 32px;
          border: none;
          border-radius: 50%;
          cursor: pointer;
          font-size: 16px;
          font-weight: bold;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }
        .approve-btn {
          background: #10b981;
          color: white;
        }
        .approve-btn:hover {
          background: #059669;
          transform: scale(1.1);
        }
        .decline-btn {
          background: #ef4444;
          color: white;
        }
        .decline-btn:hover {
          background: #dc2626;
          transform: scale(1.1);
        }
        .pagination {
          margin-top: 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          color: var(--text-primary, #4a5568);
          font-size: 14px;
        }
        .pagination-buttons {
          display: flex;
          gap: 8px;
        }
        .page-btn {
          padding: 8px 12px;
          border: 1px solid var(--border-color, #d1d5db);
          border-radius: 4px;
          background: var(--bg-card, #fff);
          cursor: pointer;
          color: var(--text-primary, #374151);
          transition: all 0.2s;
        }
        .page-btn:hover:not(.active):not(:disabled) {
          background: var(--bg-hover, #e5e7eb);
        }
        .page-btn.active {
          background: #3b82f6;
          color: #fff;
          border-color: #3b82f6;
        }
        .page-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .no-results {
          text-align: center;
          padding: 40px;
          color: #6b7280;
          font-size: 16px;
        }
        .arrow-icon {
          margin-left: 5px;
          color: #3b82f6;
        }
        /* Modal Styles */
        .profile-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.6);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
          padding: 20px;
        }
        .profile-modal-content {
          background: var(--bg-card, #ffffff);
          border-radius: 12px;
          /* Increase modal width for better readability on large screens */
          max-width: 1000px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 10px 40px var(--shadow, rgba(0, 0, 0, 0.2));
          position: relative;
        }
        .modal-close-btn {
          position: absolute;
          top: 15px;
          right: 15px;
          background: #ef4444;
          color: white;
          border: none;
          border-radius: 50%;
          width: 35px;
          height: 35px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: 18px;
          z-index: 1001;
          transition: all 0.2s;
        }
        .modal-close-btn:hover {
          background: #dc2626;
          transform: scale(1.1);
        }
        .modal-loading {
          padding: 60px 40px;
          text-align: center;
          color: var(--text-muted, #6b7280);
          display: flex;
          justify-content: center;
          align-items: center;
        }
        .modal-loading .loader {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          display: inline-block;
          position: relative;
          border: 2px solid #FF3D00;
          box-sizing: border-box;
          animation: rotation 1s linear infinite;
        }
        .modal-loading .loader::after {
          content: '';  
          box-sizing: border-box;
          position: absolute;
          left: 4px;
          top: 4px;
          border: 2px solid #000;
          width: 12px;
          height: 12px;
          border-radius: 50%;
        }
        [data-theme="dark"] .modal-loading .loader::after {
          border: 2px solid #FFF;
        }
        @keyframes rotation {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        .profile-modal-body {
          padding: 30px;
        }
        .profile-modal-body h3 {
          margin: 0 0 25px 0;
          color: var(--text-primary, #1f2937);
          font-size: 24px;
          border-bottom: 2px solid var(--border-color, #e5e7eb);
          padding-bottom: 15px;
        }
        .profile-details {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        .profile-details .profile-item {
          display: flex;
          align-items: flex-start;
          gap: 20px;
          padding: 15px;
          border-bottom: 1px solid var(--border-color, #e5e7eb);
        }
        .profile-details .profile-item:last-child {
          border-bottom: none;
        }
        .profile-details .label {
          font-weight: 600;
          color: var(--text-primary, #374151);
          width: 180px;
          flex-shrink: 0;
          font-size: 14px;
        }
        .profile-details .value {
          flex: 1;
          color: var(--text-primary, #1f2937);
          font-size: 14px;
          word-break: break-word;
        }
        .profile-details .value.description {
          white-space: pre-wrap;
          line-height: 1.6;
        }
        .profile-pic-modal {
          width: 120px;
          height: 120px;
          object-fit: cover;
          border-radius: 50%;
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        }
        .services-list-modal {
          list-style: none;
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin: 0;
          padding: 0;
        }
        .services-list-modal li {
          background: var(--bg-tertiary, #e5e7eb);
          padding: 6px 12px;
          border-radius: 6px;
          font-size: 13px;
          color: var(--text-primary, #1f2937);
        }
        .media-grid-modal {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
          gap: 15px;
          margin-top: 10px;
        }
        .media-item-container-modal {
          position: relative;
          overflow: hidden;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
        .media-item-modal {
          width: 100%;
          height: 150px;
          object-fit: cover;
          display: block;
        }
        @media (max-width: 768px) {
          .header {
            flex-direction: column;
            gap: 15px;
            align-items: flex-start;
          }
          .search-sort {
            width: 100%;
            flex-wrap: wrap;
          }
          .search-input {
            width: 100%;
          }
          .stats-grid {
            grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
            gap: 15px;
          }
          .profile-modal-content {
            max-width: 95%;
            max-height: 95vh;
          }
          .profile-modal-body {
            padding: 20px;
          }
          .profile-details .profile-item {
            flex-direction: column;
            gap: 10px;
          }
          .profile-details .label {
            width: auto;
          }
          .profile-pic-modal {
            width: 100px;
            height: 100px;
          }
          .media-grid-modal {
            grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
          }
        }
      `}</style>

            <div className="header">
                <h2>
                    <FaUsers /> Referrals Management
                </h2>
                <div className="search-sort">
                    <input
                        type="text"
                        className="search-input"
                        placeholder="Search by phone number..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <select className="sort-btn" value={sortBy} onChange={handleSortChange}>
                        <option value="createdAt">Sort By Date</option>
                        <option value="referrerName">Sort By Referrer</option>
                        <option value="referredName">Sort By Referred</option>
                    </select>
                    <button
                        onClick={fetchReferrals}
                        style={{
                            padding: '8px 12px',
                            backgroundColor: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px'
                        }}
                    >
                        Refresh
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="stats-grid">
                <div className="stat-card">
                    <FaUsers className="stat-icon" />
                    <p className="stat-value">{referrals.length}</p>
                    <p className="stat-label">Total Referrals</p>
                </div>
                <div className="stat-card">
                    <FaUserPlus className="stat-icon" />
                    <p className="stat-value">{filteredReferrals.length}</p>
                    <p className="stat-label">Filtered Results</p>
                </div>
                <div className="stat-card">
                    <FaCalendar className="stat-icon" />
                    <p className="stat-value">
                        {referrals.filter(r => {
                            const date = r.createdAt?._seconds ? new Date(r.createdAt._seconds * 1000) : new Date(r.createdAt);
                            const today = new Date();
                            return date.toDateString() === today.toDateString();
                        }).length}
                    </p>
                    <p className="stat-label">Today's Referrals</p>
                </div>
                <div className="stat-card">
                    <FaUserPlus className="stat-icon" />
                    <p className="stat-value">
                        {referrals.filter(r => r.status === 'approved').length}
                    </p>
                    <p className="stat-label">Approved Referrals</p>
                </div>
            </div>

            <div className="table-container">
                <table>
                    <thead>
                        <tr>
                            <th>S.No</th>
                            <th>Referrer Name</th>
                            <th>Referrer Profession</th>
                            <th>Referrer Phone</th>
                            <th>Referred To Name</th>
                            <th>Referred To Profession</th>
                            <th>Referred To Phone</th>
                            <th>Referral Date</th>
                            <th>Status</th>
                            <th>Reward</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedReferrals.currentReferrals.length > 0 ? (
                            paginatedReferrals.currentReferrals.map((referral, index) => {
                                const referrerServiceman = getServicemanByAuthId(referral.referrerAuthId);
                                const referredServiceman = getServicemanByAuthId(referral.newAuthId);

                                return (
                                    <tr key={referral.id} className="referral-row">
                                        <td>{paginatedReferrals.indexOfFirstReferral + index + 1}</td>
                                        <td>
                                            <div
                                                className="user-name clickable-name"
                                                onClick={() => referrerServiceman?.id && handleServicemanClick(referrerServiceman.id)}
                                                title="Click to view profile"
                                            >
                                                {referrerServiceman?.name || "Unknown Serviceman"}
                                            </div>
                                        </td>
                                        <td>
                                            <div style={{ color: '#3b82f6', fontWeight: '500' }}>
                                                {referrerServiceman?.profession || "N/A"}
                                            </div>
                                        </td>
                                        <td>{referrerServiceman?.phone ? `+91-${referrerServiceman.phone}` : "N/A"}</td>
                                        <td>
                                            <div
                                                className="user-name clickable-name"
                                                onClick={() => referredServiceman?.id && handleServicemanClick(referredServiceman.id)}
                                                title="Click to view profile"
                                            >
                                                {referredServiceman?.name || "Unknown Serviceman"}
                                            </div>
                                        </td>
                                        <td>
                                            <div style={{ color: '#3b82f6', fontWeight: '500' }}>
                                                {referredServiceman?.profession || "N/A"}
                                            </div>
                                        </td>
                                        <td>{referral.newUserPhone ? `+91-${referral.newUserPhone}` : "N/A"}</td>
                                        <td>{formatDate(referral.createdAt)}</td>
                                        <td>
                                            <span className={`status-badge ${referral.status || 'pending'}`}>
                                                {referral.status || 'Pending'}
                                            </span>
                                        </td>
                                        <td>
                                            {referral.reward ? `₹${referral.reward}` : 'N/A'}
                                        </td>
                                        <td>
                                            <div className="action-buttons">
                                                {referral.status !== 'approved' && (
                                                    <button
                                                        className="approve-btn"
                                                        onClick={() => handleApproveReferral(referral.id)}
                                                        title="Approve Referral"
                                                    >
                                                        ✓
                                                    </button>
                                                )}
                                                {referral.status !== 'declined' && (
                                                    <button
                                                        className="decline-btn"
                                                        onClick={() => handleDeclineReferral(referral.id)}
                                                        title="Decline Referral"
                                                    >
                                                        ✗
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="11" className="no-results">
                                    {referrals.length === 0 ? (
                                        <div>
                                            <p>No referrals found in the database</p>
                                            <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '10px' }}>
                                                Debug Info: Referrals count: {referrals.length}, Servicemen count: {servicemen.length}
                                            </p>
                                        </div>
                                    ) : (
                                        "No referrals found matching your search"
                                    )}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="pagination">
                <span>Showing {filteredReferrals.length} Entries →</span>
                <div className="pagination-buttons">
                    <button
                        className="page-btn"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                    >
                        Previous
                    </button>
                    {Array.from({ length: paginatedReferrals.totalPages }, (_, i) => (
                        <button
                            key={i + 1}
                            className={`page-btn ${currentPage === i + 1 ? "active" : ""}`}
                            onClick={() => handlePageChange(i + 1)}
                        >
                            {i + 1}
                        </button>
                    ))}
                    <button
                        className="page-btn"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === paginatedReferrals.totalPages}
                    >
                        Next
                    </button>
                </div>
            </div>

            {/* Serviceman Profile Modal */}
            {(selectedServiceman || modalLoading) && (
                <div className="profile-modal-overlay" onClick={closeModal}>
                    <div className="profile-modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close-btn" onClick={closeModal}>
                            <FaTimes />
                        </button>
                        {modalLoading ? (
                            <div className="modal-loading">
                                <span className="loader"></span>
                            </div>
                        ) : selectedServiceman ? (
                            <div className="profile-modal-body">
                                <h3>Serviceman Profile</h3>
                                <div className="profile-details">
                                    <div className="profile-item">
                                        <span className="label">Profile Picture</span>
                                        <div className="value">
                                            <img
                                                src={selectedServiceman.image || DEFAULT_IMAGE}
                                                alt="Profile"
                                                className="profile-pic-modal"
                                                onError={(e) => {
                                                    e.target.src = DEFAULT_IMAGE;
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="profile-item">
                                        <span className="label">Name</span>
                                        <span className="value">{selectedServiceman.name || "N/A"}</span>
                                    </div>
                                    <div className="profile-item">
                                        <span className="label">Email</span>
                                        <span className="value">{selectedServiceman.email || "N/A"}</span>
                                    </div>
                                    <div className="profile-item">
                                        <span className="label">Phone</span>
                                        <span className="value">{selectedServiceman.phone ? `+91-${selectedServiceman.phone}` : "N/A"}</span>
                                    </div>
                                    <div className="profile-item">
                                        <span className="label">Profession</span>
                                        <span className="value">{selectedServiceman.profession || "N/A"}</span>
                                    </div>
                                    <div className="profile-item">
                                        <span className="label">Date of Birth</span>
                                        <span className="value">
                                            {selectedServiceman.dateOfBirth?._seconds
                                                ? new Date(selectedServiceman.dateOfBirth._seconds * 1000).toLocaleDateString()
                                                : selectedServiceman.dateOfBirth || "N/A"}
                                        </span>
                                    </div>
                                    <div className="profile-item">
                                        <span className="label">Location</span>
                                        <span className="value">{selectedServiceman.location || selectedServiceman.address || "N/A"}</span>
                                    </div>
                                    <div className="profile-item">
                                        <span className="label">Primary Area</span>
                                        <span className="value">{selectedServiceman.areaName || "N/A"}</span>
                                    </div>
                                    <div className="profile-item">
                                        <span className="label">Device Type</span>
                                        <span className="value">{selectedServiceman.deviceType && selectedServiceman.deviceType.trim() !== '' ? selectedServiceman.deviceType : "N/A"}</span>
                                    </div>
                                    <div className="profile-item">
                                        <span className="label">Services</span>
                                        <div className="value">
                                            {selectedServiceman.services?.length > 0 ? (
                                                <ul className="services-list-modal">
                                                    {selectedServiceman.services.map((service, idx) => (
                                                        <li key={idx}>{service}</li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                "No services listed"
                                            )}
                                        </div>
                                    </div>
                                    <div className="profile-item">
                                        <span className="label">Availability (Start-End)</span>
                                        <span className="value">
                                            {selectedServiceman.availabilityStart?._seconds && selectedServiceman.availabilityEnd?._seconds
                                                ? `${new Date(selectedServiceman.availabilityStart._seconds * 1000).toLocaleTimeString()} - ${new Date(selectedServiceman.availabilityEnd._seconds * 1000).toLocaleTimeString()}`
                                                : "N/A"}
                                        </span>
                                    </div>
                                    <div className="profile-item">
                                        <span className="label">Average Rating</span>
                                        <span className="value">{selectedServiceman.avgRating ? `${selectedServiceman.avgRating}/5` : "N/A"}</span>
                                    </div>
                                    <div className="profile-item">
                                        <span className="label">Review Count</span>
                                        <span className="value">{selectedServiceman.reviewCount || 0}</span>
                                    </div>
                                    <div className="profile-item">
                                        <span className="label">Approval Status</span>
                                        <span className={`value ${isServicemanApproved(selectedServiceman) ? 'approved-status' : 'not-approved-status'}`}>
                                            {isServicemanApproved(selectedServiceman) ? 'Approved' : 'Not Approved'}
                                        </span>
                                    </div>
                                    <div className="profile-item">
                                        <span className="label">Description</span>
                                        <span className="value description">{selectedServiceman.description || "N/A"}</span>
                                    </div>
                                    {selectedServiceman.media && selectedServiceman.media.length > 0 && (
                                        <div className="profile-item">
                                            <span className="label">Media Gallery</span>
                                            <div className="value">
                                                <div className="media-grid-modal">
                                                    {selectedServiceman.media.map((item, idx) => (
                                                        <div key={idx} className="media-item-container-modal">
                                                            {item.type === "image" ? (
                                                                <img
                                                                    src={item.url}
                                                                    alt={`Media ${idx + 1}`}
                                                                    className="media-item-modal"
                                                                    onError={(e) => {
                                                                        e.target.src = DEFAULT_IMAGE;
                                                                    }}
                                                                />
                                                            ) : (
                                                                <video
                                                                    src={item.url}
                                                                    controls
                                                                    className="media-item-modal"
                                                                />
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Referrals;